<template>
  <Container indent="none" class="min-h-screen">
    <div class="bg-gray-10">
      <div class="mx-auto max-w-screen-lg py-12 lg:py-44">
        <div class="flex items-baseline">
          <h1
            class="border-r border-black pr-4 text-2xl font-bold text-gray-500 lg:text-3xl"
          >
            {{ $t('form.register.title') }}
          </h1>
          <AppLink
            :to="localePath('/shop/login')"
            :underline="false"
            class="pl-4"
          >
            {{ $t('form.login.title') }}
          </AppLink>
        </div>
        <Bard
          class="mt-4 font-medium max-md:text-sm"
          :content="registerPageSettings?.text"
        />
        <div class="mt-8 grid gap-4 lg:grid-cols-2">
          <div
            v-for="customerType in [
              CustomerType.Professional,
              CustomerType.Individual,
            ]"
            :key="`customer-type-${customerType}`"
            class="relative cursor-pointer border bg-white p-6"
            :class="{
              'border-gray-500': type === customerType,
              'border-transparent': type !== customerType,
            }"
            @click="changeType(customerType)"
          >
            <input
              v-model="type"
              :value="customerType"
              type="radio"
              class="absolute right-4 top-4 size-3 accent-gray-500"
            />
            <AppImage
              v-if="registerPageSettings?.[`box_${customerType}_image`]"
              :asset="registerPageSettings?.[`box_${customerType}_image`]"
              class="h-24 w-auto"
            />
            <h2 class="mb-2 font-bold">
              {{ registerPageSettings?.[`box_${customerType}_title`] }}
            </h2>
            <p class="text-sm font-medium text-gray-300 lg:text-xs">
              {{ registerPageSettings?.[`box_${customerType}_text`] }}
            </p>
            <ul
              v-if="
                registerPageSettings?.[`box_${customerType}_advantages`]?.length
              "
              class="mt-4"
            >
              <li
                v-for="advantage in registerPageSettings?.[
                  `box_${customerType}_advantages`
                ]"
                :key="`advantage-${advantage}`"
                class="mt-2 flex items-center gap-2"
              >
                <IconCheck
                  class="size-5 rounded-full bg-green-100 text-green-500"
                />
                <span class="text-sm font-bold lg:text-xs">
                  {{ advantage }}
                </span>
              </li>
            </ul>
          </div>
        </div>
        <Bard
          class="mt-8 text-sm font-medium"
          :content="registerPageSettings?.further_information"
        />
        <AppButton class="mt-12" @click="submit">
          {{ $t('form.register.submit') }}
        </AppButton>
      </div>
    </div>
  </Container>
</template>

<script setup lang="ts">
import { CustomerType } from '~/types/general'
import { useRegistrationStore } from '~/stores/registration'

const localePath = useLocalePath()
const router = useRouter()
const registration = useRegistrationStore()
const cms = inject(CMS_GLOBALS)
const { t } = useI18n()

const { state } = storeToRefs(registration)

const registerPageSettings = computed(() => cms.value.registerPageSettings)

const type = ref<CustomerType>(state.value.type || CustomerType.Professional)

const changeType = (newType: CustomerType) => {
  type.value = newType
}

const submit = async () => {
  registration.reset()
  state.value.type = type.value
  await router.push(localePath(`/shop/register/${type.value}`))
}

useSeoMeta({
  title: t('form.register.title'),
  description: registerPageSettings.value?.text,
})
</script>
